import { CitationProps } from './CitationPanel';

export interface SummaryItemProps {
  summaryType:
    | 'overview'
    | 'cancerHistory'
    | 'pastMedicalHistory'
    | 'medicationsAndAllergies'
    | 'familyHistory'
    | 'socialHistory';
  title: string;
  contentType: 'standard' | 'compact';
  contents?: SummaryContent[];
  onRelatedItemClick: (content: CitationItem) => void;
  onCitationGroupSelection: (selectedGroupId: number | null) => void;
  selectedCitationGroupId: number | null;
  traceId?: string;
  spanId?: string;
  userRoleData?: {
    user?: {
      id: string;
      isSuperuser: boolean;
    };
  };
  patientId?: string;
}

export interface SummaryContent {
  id?: number;
  text: string;
  citations: CitationItem[];
  subItems?: SummaryContent[];
}
export interface SummaryDataType {
  summaryType: string;
  standardContent: SummaryContent[];
  compactContent: SummaryContent[];
}

export interface ServerGeoJSONObject {
  type: string;
  coordinates: number[][][] | number[][][][];
}
export interface ServerHighlightGeom {
  pageNumber: number;
  geometry: ServerGeoJSONObject;
}
export interface ServerCitationItem {
  resourceType: string;
  resourceId: string;
  resourceName: string;
  title?: string;
  citedText: string;
  page?: number;
  dateOfDocument?: string;
  highlightGeoms?: ServerHighlightGeom[];
  authorName?: string;
  status?: string;
}

export interface ServerSummaryData {
  overview: ServerSummaryContent;
  cancerHistory: ServerSummaryContent;
  medicalHistory: ServerSummaryContent;
  medications: ServerSummaryContent;
  familyHistory: ServerSummaryContent;
  socialHistory: ServerSummaryContent;
}

export interface ServerSummaryGenericContent {
  text: string;
  citations: ServerCitationItem[];
  subItems?: ServerSummaryGenericContent[];
}

export interface ServerSummaryContent {
  standard: ServerSummaryGenericContent[];
  compact: ServerSummaryGenericContent[];
}

export interface ServerTimelineEventCitation {
  status?: string;
  title: string;
  documentUrl: string;
  documentType: string;
  dateOfDocument: string;
  notes?: string;
  highlightGeoms?: ServerHighlightGeom[];
  citedText?: string;
  authorName?: string;
}

export interface ServerTimelineEventSummary {
  text: string;
  citations: ServerTimelineEventCitation[];
  subItems?: ServerTimelineEventSummary[];
}

export interface ServerTimelineEvent {
  type: string;
  date: string;
  title: string;
  isLatest: boolean;
  isNew: boolean;
  resourceId?: number;
  traceId?: string;
  spanId?: string;
  standard: ServerTimelineEventSummary[];
  compact: ServerTimelineEventSummary[];
}

export interface Snippet {
  page: string;
  text: string;
}
export interface CitationItem {
  title: string;
  documentUrl?: string;
  documentType: string;
  dateOfDocument?: string;
  icon?: React.ReactNode;
  notes?: string;
  status?: string;
  relatedItemId?: number;
  selected?: boolean;
  snippets?: Snippet[];
  highlightGeoms?: ServerHighlightGeom[];
  citedText?: string;
  author?: string;
}

export interface SummarySettings {
  showOverview: boolean;
  showCancerHistory: boolean;
  showPastMedicalHistory: boolean;
  showMedicationsAndAllergies: boolean;
  showFamilyHistory: boolean;
  showSocialHistory: boolean;
}

export interface TimelineEventSummary {
  id?: number;
  text: string;
  citations: CitationItem[];
  subItems?: TimelineEventSummary[];
}
export interface TimelineEvent {
  type: string;
  date: string;
  title: string;
  resourceId?: number;
  traceId?: string;
  spanId?: string;
  standardContent: TimelineEventSummary[];
  compactContent: TimelineEventSummary[];
  isLatest?: boolean;
  isNew?: boolean;
}
export interface HealthTimelineSettings {
  showImagingReports: boolean;
  showPathologyReports: boolean;
  showBloodTest: boolean;
  showPatientReported: boolean;
  showClinicalNotes: boolean;
  showLetters: boolean;
}

export interface ChatRequest {
  text: string;
  createdAt: string;
  responseCreatedAt?: string;
  type: string;
  response: string;
  traceId?: string;
  spanId?: string;
  citations: CitationProps[];
  /** For document types (hpi_document, etc.), the actual document HTML content */
  letterHtml?: string;
  /** Whether this is an edit operation (true) or a new document generation (false/undefined) */
  isEdit?: boolean;
  /** Status text to show during processing (e.g., "Analyzing your request...") */
  statusText?: string;
  /** Reasoning text shown in a collapsible thinking box while the model is deliberating */
  reasoningText?: string;
}
export interface ChatHistoryItem {
  title: string;
  requests: ChatRequest[];
  conversationId?: number;
  kind?: 'separator' | 'conversation' | 'new';
}

export const NEW_CONVERSATION_PLACEHOLDER: ChatHistoryItem = {
  title: 'New Conversation',
  requests: [],
  kind: 'new',
};
